import {
  type LessonItem,
  type SchoolEvent,
  type StudentGrade,
  addDays,
  canJoinLesson,
  canOpenClassBoard,
  gradeLevel,
  termFor,
  timeOf,
  todayIso,
  useBackpack,
  useEvents,
  useStudentHomework,
  useStudentRecord,
  useTerms,
} from '@shkola/api';
import { subjectLook, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import {
  Backpack,
  BookOpen,
  CalendarDays,
  DoorOpen,
  NotebookPen,
  User,
  Video,
  X,
} from 'lucide-react';
import {
  type KeyboardEvent as KeyEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ConfettiIcon,
  type FilledIcon,
  ForkKnifeIcon,
  HourglassIcon,
  VideoCameraIcon,
} from '../../design/icons';
import { useLook } from '../../design/looks';
import { LiveDot, OnColorProvider, StatusPill, buttonClass, useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { useMinute } from '../../lib/now';
import { BackpackArt } from './Art';
import {
  LEVEL_TONES,
  type Slot,
  type StudentContext,
  capitalize,
  dayLong,
  dayNow,
  metaOf,
  minutesBetween,
  plain,
  roomOf,
  slotsOf,
  useShownDay,
} from './model';

/*
 * «Сьогодні» / «Завтра» (UX of 26.10 on the look of 27.10, ref. «Monday, June 3»): the lessons of the
 * day the home shows as coloured rows of their subjects — the subject, the room and the teacher, the
 * time on the right; a timeline beside them that the time gone by fills, with the knob of «now»; the
 * breaks with their length and time, the one going on in colour; marks only for what changed (a
 * substitute, cancelled, online, in the shelter, a test); the grade got at a lesson right on it; the
 * school's events of the day; for the next day — what to pack. A click on a lesson opens its card.
 */

/** The marks of a lesson: only what is not as usual. */
function Changes({ lesson }: { lesson: LessonItem }) {
  const { t } = useTranslation();
  if (lesson.status === 'cancelled')
    return (
      <StatusPill tone="grey" size="xs">
        {t('studentHome.cancelled')}
      </StatusPill>
    );
  return (
    <>
      {lesson.isTest && (
        <StatusPill tone="red" size="xs">
          {t('studentHome.test')}
        </StatusPill>
      )}
      {lesson.substitute && (
        <StatusPill tone="orange" size="xs">
          {t('studentHome.substitute')}
        </StatusPill>
      )}
      {lesson.format === 'online' && (
        <StatusPill tone="violet" size="xs" icon={VideoCameraIcon}>
          {t('studentHome.online')}
        </StatusPill>
      )}
      {lesson.format === 'shelter' && (
        <StatusPill tone="yellow" size="xs">
          {t('studentHome.shelter')}
        </StatusPill>
      )}
    </>
  );
}

/** A grade got at a lesson, in its level's colour. */
function GradeMark({ grade }: { grade: StudentGrade }) {
  const { t } = useTranslation();
  const tone =
    grade.kind === 'thematic' || grade.kind === 'semester' || grade.kind === 'year'
      ? 'violet'
      : grade.value
        ? (LEVEL_TONES[gradeLevel(grade.value)] ?? 'grey')
        : 'grey';
  return (
    <OnColorProvider value>
      <StatusPill tone={tone} size="sm" className="min-w-7 justify-center">
        {gradeShort(t, grade.value, grade.special)}
      </StatusPill>
    </OnColorProvider>
  );
}

const onKeys = (open: () => void) => (e: KeyEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    open();
  }
};

type NodeState = 'done' | 'next' | 'ahead' | 'off';

/**
 * The timeline beside a row: a track that the time gone by fills (navy), a point per lesson, the knob
 * of «now». The track runs from the first point to the last.
 */
export function Rail({
  fill,
  node,
  first,
  last,
  nowAt,
}: {
  /** 0…1: how much of this row's time is gone */
  fill: number;
  node?: NodeState;
  first: boolean;
  last: boolean;
  /** 0…1: where «now» is inside this row */
  nowAt?: number;
}) {
  const top = first ? 50 : 0;
  const bottom = last ? 50 : 100;
  const filled = top + (bottom - top) * Math.min(1, Math.max(0, fill));
  return (
    <span aria-hidden className="relative w-4 shrink-0 self-stretch">
      {bottom > top && (
        <span
          className="absolute left-1/2 w-0.5 -translate-x-1/2 bg-[#E1E6EE]"
          style={{ top: `${top}%`, bottom: `${100 - bottom}%` }}
        />
      )}
      {filled > top && (
        <span
          className="absolute left-1/2 w-0.5 -translate-x-1/2 bg-primary"
          style={{ top: `${top}%`, height: `${filled - top}%` }}
        />
      )}
      {node && (
        <span
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
            node === 'done'
              ? 'size-2 bg-primary'
              : node === 'next'
                ? 'size-2.5 bg-white ring-2 ring-primary'
                : node === 'ahead'
                  ? 'size-2 bg-white ring-2 ring-[#C9D1DC]'
                  : 'size-2 bg-white ring-2 ring-[#E1E6EE]'
          }`}
        />
      )}
      {nowAt !== undefined && (
        <span
          className="absolute left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_0_3px_#fff,0_1px_4px_rgb(24_29_51/0.35)]"
          style={{ top: `${top + (bottom - top) * Math.min(1, Math.max(0, nowAt))}%` }}
        />
      )}
    </span>
  );
}

/** A lesson as a row in its subject's colour (ref. «Design Research 9:45 / 11:00»). */
export function LessonRow({
  lesson,
  state,
  now,
  isToday,
  grades,
  onOpen,
}: {
  lesson: LessonItem;
  state: 'done' | 'live' | 'next' | 'ahead';
  now: number;
  isToday: boolean;
  grades: StudentGrade[];
  onOpen: (lesson: LessonItem, el: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const tone = tones[subjectLook(lesson.subject).tone];
  const cancelled = lesson.status === 'cancelled';
  const start = Date.parse(lesson.startsAt);
  const end = Date.parse(lesson.endsAt);
  const join = isToday && state !== 'done' && canJoinLesson(lesson, todayIso()) && end > now;
  const open = () => ref.current && onOpen(lesson, ref.current);
  const ink = cancelled ? tones.grey.ink : tone.ink;
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeys(open)}
      className={`lesson-row relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-[14px] py-2.5 pr-3.5 pl-2.5 text-left transition-[filter,opacity] outline-none hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-primary/40 ${cancelled ? 'opacity-50' : state === 'done' ? 'opacity-70 grayscale' : ''}`}
      style={{
        backgroundColor: cancelled ? tones.grey.soft : tone.soft,
        boxShadow: state === 'live' ? `inset 0 0 0 1.5px ${tone.icon}66` : undefined,
      }}
    >
      <span
        className="w-1 shrink-0 self-stretch rounded-full"
        style={{ backgroundColor: cancelled ? tones.grey.icon : tone.icon }}
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`truncate text-[15px] leading-5 font-semibold ${cancelled ? 'line-through' : ''}`}
            style={{ color: ink }}
          >
            {lesson.subject}
          </span>
          {/* a mark on a coloured row is white with coloured text (his rule) */}
          <span className="hidden shrink-0 items-center gap-1 sm:flex">
            <OnColorProvider value>
              <Changes lesson={lesson} />
            </OnColorProvider>
          </span>
        </span>
        <span
          className="mt-0.5 block truncate text-[13px] leading-[18px]"
          style={{ color: ink, opacity: 0.72 }}
        >
          {metaOf(t, lesson)}
        </span>
      </span>
      {grades.map((g) => (
        <GradeMark key={g.id} grade={g} />
      ))}
      <OnColorProvider value>
        {state === 'live' && (
          <span className="hidden shrink-0 sm:flex">
            <StatusPill tone="green" dot size="xs">
              {t('studentHome.now', { min: Math.max(1, Math.ceil((end - now) / 60_000)) })}
            </StatusPill>
          </span>
        )}
        {state === 'next' && isToday && (
          <span className="hidden shrink-0 sm:flex">
            <StatusPill tone="blue" size="xs">
              {t('studentHome.day.nextIn', { min: Math.max(1, minutesBetween(now, start)) })}
            </StatusPill>
          </span>
        )}
      </OnColorProvider>
      {join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: lesson.id }}
          onClick={(e) => e.stopPropagation()}
          className={buttonClass('primary', 'sm')}
        >
          {t('student.lessons.join')}
        </Link>
      )}
      <span className="flex shrink-0 flex-col items-end">
        <span className="text-[14px] leading-5 font-semibold" style={{ color: ink }}>
          {timeOf(lesson.startsAt)}
        </span>
        <span className="text-[12.5px] leading-[18px]" style={{ color: ink, opacity: 0.6 }}>
          {timeOf(lesson.endsAt)}
        </span>
      </span>
      {state === 'live' && (
        <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/70">
          <span
            className="block h-full"
            style={{
              width: `${Math.round(((now - start) / (end - start)) * 100)}%`,
              backgroundColor: tone.icon,
            }}
          />
        </span>
      )}
    </div>
  );
}

/** A break between two lessons: how long, from when to when; the one going on in colour. */
export function BreakRow({
  slot,
  now,
  current,
}: {
  slot: Extract<Slot, { kind: 'break' }>;
  now: number;
  current: boolean;
}) {
  const { t } = useTranslation();
  const big = slot.minutes >= 20;
  const Icon = big && slot.minutes < 40 ? ForkKnifeIcon : HourglassIcon;
  const label = current
    ? t('studentHome.day.breakGoesOn', { min: Math.max(1, Math.ceil((slot.end - now) / 60_000)) })
    : capitalize(
        slot.minutes >= 40
          ? t('studentHome.day.window', { min: slot.minutes })
          : big
            ? t('studentHome.day.bigBreak', { min: slot.minutes })
            : t('studentHome.day.break', { min: slot.minutes }),
      );
  const range = `${timeOf(new Date(slot.start).toISOString())} – ${timeOf(new Date(slot.end).toISOString())}`;
  return (
    <div
      className="break-row my-1 flex h-8 min-w-0 items-center gap-2 rounded-[12px] px-3"
      style={current ? { backgroundColor: tones.teal.soft } : undefined}
    >
      {current ? (
        <LiveDot color={tones.teal.vivid} />
      ) : (
        <Icon size={14} weight="fill" aria-hidden color="#AEB6C2" />
      )}
      <span
        className="truncate text-[13px] font-medium"
        style={{ color: current ? tones.teal.vivid : '#6B7380' }}
      >
        {label}
      </span>
      <span
        className="ml-auto shrink-0 text-[12.5px]"
        style={{ color: current ? tones.teal.vivid : '#9AA2AE' }}
      >
        {range}
      </span>
    </div>
  );
}

/** A line of the day without a lesson: before the first one, after the last one. */
export function EdgeRow({
  icon: Icon,
  tone,
  live,
  children,
}: {
  icon?: FilledIcon;
  tone: 'teal' | 'green';
  live?: boolean;
  children: ReactNode;
}) {
  const tint = tones[tone];
  return (
    <div
      className="break-row my-1 flex h-8 min-w-0 items-center gap-2 rounded-[12px] px-3"
      style={{ backgroundColor: tint.soft, color: tint.vivid }}
    >
      {live ? <LiveDot color={tint.vivid} /> : Icon && <Icon size={14} weight="fill" aria-hidden />}
      <span className="truncate text-[13px] font-medium">{children}</span>
    </div>
  );
}

/** The school's events on a day, for the student's class or the whole school. */
export function useDayEvents(ctx: StudentContext, date: string | undefined): SchoolEvent[] {
  const events = useEvents(ctx.schoolId, todayIso());
  if (!date) return [];
  return (events.data ?? []).filter(
    (e) =>
      e.startsOn <= date &&
      date <= e.endsOn &&
      e.audience !== 'staff' &&
      (e.classId === null || e.classId === ctx.child.class?.id),
  );
}

/** What to pack for the day shown (ref. «Day in history»): the things as chips, the backpack. */
export function PackPanel({ ctx, date }: { ctx: StudentContext; date: string }) {
  const { t } = useTranslation();
  const pack = useBackpack(ctx.schoolId, ctx.child.memberId);
  if (pack.date !== date) return null;
  const tint = tones.yellow;
  return (
    <OnColorProvider value>
      <div
        className="relative mt-4 overflow-hidden rounded-[20px] p-4 pr-28"
        style={{ backgroundImage: `linear-gradient(160deg, ${tint.from} 0%, ${tint.to} 100%)` }}
      >
        <BackpackArt tone="yellow" className="absolute -right-1 -bottom-3 w-[104px]" />
        <p className="text-[14px] leading-5 font-semibold text-ink">{t('studentHome.day.pack')}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {pack.items.length > 0 ? (
            pack.items.map((i) => (
              <StatusPill key={i.label} tone="yellow" size="md">
                {plain(i.label)}
                {i.fromTeacher && (
                  <span className="font-medium opacity-75">· {t('backpack.teacher')}</span>
                )}
              </StatusPill>
            ))
          ) : (
            <span className="text-[14px]" style={{ color: tint.ink }}>
              {t('studentHome.onlyBooks')}
            </span>
          )}
        </div>
      </div>
    </OnColorProvider>
  );
}

/** The grades got at the lessons of a day, by lesson. */
export function useLessonGrades(ctx: StudentContext, date: string | undefined) {
  const terms = useTerms(ctx.schoolId);
  const term = terms.data && date ? termFor(terms.data, date) : undefined;
  const record = useStudentRecord(ctx.child.memberId, term?.id);
  return (l: LessonItem): StudentGrade[] =>
    (record.data?.grades ?? []).filter((g) => g.lessonId === l.id);
}

/**
 * How tall the day's block is, px (the rows are of a fixed height): the cards beside it are chosen to
 * fill the same height.
 */
export function useDayHeight(ctx: StudentContext): number {
  const now = useMinute();
  const day = useShownDay(ctx);
  const events = useDayEvents(ctx, day.date);
  const pack = useBackpack(ctx.schoolId, ctx.child.memberId);
  if (day.pending || !day.date) return 0;
  const status = dayNow(day.lessons, now);
  const slots = slotsOf(day.lessons);
  const lessons = slots.filter((s) => s.kind === 'lesson').length;
  const edges = day.isToday ? Number(status.before) + Number(status.over) : 0;
  return (
    48 +
    48 +
    (events.length > 0 ? events.length * 46 + 2 : 0) +
    lessons * 66 +
    (slots.length - lessons + edges) * 40 +
    (!day.isToday && pack.date === day.date ? 120 : 0)
  );
}

export function DayBlock({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { t } = useTranslation();
  const now = useMinute();
  const look = useLook();
  const age = useAge();
  const day = useShownDay(ctx);
  const [open, setOpen] = useState<{ lesson: LessonItem; el: HTMLElement }>();
  const [unfolded, setUnfolded] = useState(false);
  const gradesOf = useLessonGrades(ctx, day.date);
  const events = useDayEvents(ctx, day.date);
  const today = todayIso();
  const tomorrow = addDays(today, 1);

  const title = !day.date
    ? t('studentHome.day.title')
    : day.isToday
      ? t('studentHome.day.today')
      : day.date === tomorrow
        ? t('studentHome.day.tomorrow')
        : dayLong(day.date);
  const status = dayNow(day.lessons, now);
  const held = status.held;
  const slots = slotsOf(day.lessons);

  type Row =
    | { key: string; kind: 'before' }
    | { key: string; kind: 'over' }
    | { key: string; kind: 'slot'; slot: Slot; index: number };
  const rows: Row[] = [
    ...(day.isToday && status.before ? [{ key: 'before', kind: 'before' as const }] : []),
    ...slots.map((slot, index) => ({
      key: slot.kind === 'lesson' ? slot.lesson.id : `break-${slot.start}`,
      kind: 'slot' as const,
      slot,
      index,
    })),
    ...(day.isToday && status.over ? [{ key: 'over', kind: 'over' as const }] : []),
  ];

  // on a phone: from what goes on now (the lesson or the break) three lessons on, the rest folded
  const focus = day.isToday
    ? Math.max(
        0,
        slots.findIndex(
          (s) =>
            (s.start <= now && now < s.end) || (s.kind === 'lesson' && s.lesson === status.next),
        ),
      )
    : 0;
  const lessonIndexes = slots
    .map((s, i) => (s.kind === 'lesson' ? i : -1))
    .filter((i) => i >= 0 && i >= focus);
  const lastShown = lessonIndexes[2] ?? slots.length - 1;
  const folded = (i: number) => !unfolded && (i < focus || i > lastShown);
  const foldedCount = slots.filter((s, i) => s.kind === 'lesson' && folded(i)).length;
  const ring = look === 'warm' ? 'ring-[#EFE8E3]' : 'ring-[#E4EAF2]';
  // «Скло»: a white card with bigger corners, so the coloured rows stand out of the grey ground
  const frame =
    look === 'glass'
      ? 'rounded-[32px] bg-white p-4 shadow-[0_18px_40px_-28px_rgb(30_42_94/0.35)] md:p-6 [&_.break-row]:my-0 [&_.break-row]:h-7 [&_.lesson-row]:py-1.5 [&_.lesson-slot]:py-[2px]'
      : `rounded-[28px] bg-white p-4 ring-1 ring-inset md:p-6 ${ring}`;

  return (
    <section className={`flex min-w-0 flex-col ${frame} ${className}`}>
      <header className="mb-4 flex min-h-8 items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5 pt-1">
          <h2
            className={`font-display font-semibold tracking-[-0.01em] text-ink ${age === 'junior' ? 'text-[22px] leading-7' : 'text-[18px] leading-6'}`}
          >
            {title}
          </h2>
          {held[0] && (
            <span className="text-[14px] text-muted">
              {!day.isToday && day.date && day.date === tomorrow && `${dayLong(day.date)} · `}
              {t('student.lessonsCount', { count: held.length })}, {timeOf(held[0].startsAt)} –{' '}
              {timeOf((held.at(-1) as LessonItem).endsAt)}
            </span>
          )}
        </div>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {day.isToday && look !== 'glass' && (
            <StatusPill tone="blue" icon={ClockIcon} size="xs">
              {t('studentHome.day.clock', { time: kyivClock.format(new Date(now)) })}
            </StatusPill>
          )}
          <Link
            to="/schedule"
            aria-label={t('studentHome.day.weekButton')}
            title={t('studentHome.day.weekButton')}
            className="inline-flex size-8 items-center justify-center rounded-full bg-white text-ink ring-1 ring-stroke transition-transform duration-500 ease-spring ring-inset hover:rotate-45"
          >
            <ArrowUpRightIcon size={15} weight="bold" aria-hidden />
          </Link>
        </span>
      </header>

      {day.pending ? (
        <div className="flex flex-col gap-2" aria-busy>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="h-[60px] animate-pulse rounded-[14px] bg-soft" />
          ))}
        </div>
      ) : !day.date ? (
        <p className="py-6 text-center text-[14px] text-muted">{t('studentHome.day.nothing')}</p>
      ) : (
        <>
          {events.length > 0 && (
            <ul className="mb-2 flex flex-col gap-1.5">
              {events.map((e) => (
                <li key={e.id}>
                  <Link
                    to="/events"
                    className="flex items-center gap-2.5 rounded-[14px] px-3 py-2.5 transition-[filter] hover:brightness-[0.98]"
                    style={{ backgroundColor: tones.pink.soft }}
                  >
                    <ConfettiIcon
                      size={16}
                      weight="fill"
                      aria-hidden
                      className="shrink-0"
                      color={tones.pink.vivid}
                    />
                    <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">
                      {e.title}
                      {e.place && (
                        <span className="font-normal" style={{ color: tones.pink.ink }}>
                          {' '}
                          · {e.place}
                        </span>
                      )}
                    </span>
                    {e.atTime && (
                      <span
                        className="shrink-0 text-[14px] font-semibold"
                        style={{ color: tones.pink.ink }}
                      >
                        {e.atTime}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <ol>
            {rows.map((row, i) => {
              const isFirst = i === 0;
              const isLast = i === rows.length - 1;
              if (row.kind === 'before' && status.first)
                return (
                  <li key={row.key} className="flex gap-3">
                    <Rail fill={0} first={isFirst} last={isLast} nowAt={0.5} />
                    <div className="min-w-0 flex-1">
                      <EdgeRow tone="teal" live>
                        {t('studentHome.day.beforeFull', {
                          min: minutesBetween(now, Date.parse(status.first.startsAt)),
                        })}
                      </EdgeRow>
                    </div>
                  </li>
                );
              if (row.kind === 'over' && status.last)
                return (
                  <li key={row.key} className="flex gap-3">
                    <Rail fill={1} first={isFirst} last={isLast} nowAt={0.5} />
                    <div className="min-w-0 flex-1">
                      <EdgeRow tone="green" icon={CheckCircleIcon}>
                        {t('studentHome.day.over', { time: timeOf(status.last.endsAt) })}
                      </EdgeRow>
                    </div>
                  </li>
                );
              if (row.kind !== 'slot') return null;
              const { slot } = row;
              const hidden = folded(row.index) ? 'max-md:hidden' : '';
              const gone = day.isToday ? (now - slot.start) / (slot.end - slot.start) : 0;
              const inside = day.isToday && slot.start <= now && now < slot.end;
              if (slot.kind === 'break')
                return (
                  <li key={row.key} className={`flex gap-3 ${hidden}`}>
                    <Rail
                      fill={gone}
                      first={isFirst}
                      last={isLast}
                      nowAt={inside ? gone : undefined}
                    />
                    <div className="min-w-0 flex-1">
                      <BreakRow slot={slot} now={now} current={inside} />
                    </div>
                  </li>
                );
              const l = slot.lesson;
              const cancelled = l.status === 'cancelled';
              const state = cancelled
                ? 'ahead'
                : l === status.live && day.isToday
                  ? 'live'
                  : day.isToday && slot.end <= now
                    ? 'done'
                    : (day.isToday && l === status.next) || (!day.isToday && l === held[0])
                      ? 'next'
                      : 'ahead';
              return (
                <li key={row.key} className={`flex gap-3 ${hidden}`}>
                  <Rail
                    fill={gone}
                    first={isFirst}
                    last={isLast}
                    node={state === 'live' ? undefined : cancelled ? 'off' : state}
                    nowAt={state === 'live' ? gone : undefined}
                  />
                  <div className="lesson-slot min-w-0 flex-1 py-[3px]">
                    <LessonRow
                      lesson={l}
                      state={state}
                      now={now}
                      isToday={day.isToday}
                      grades={gradesOf(l)}
                      onOpen={(lesson, el) => setOpen({ lesson, el })}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
          {foldedCount > 0 && (
            <button
              type="button"
              onClick={() => setUnfolded(true)}
              className="mt-2 ml-7 self-start rounded-full bg-soft px-3.5 py-1.5 text-[13px] font-semibold text-ink-soft hover:text-ink md:hidden"
            >
              {t('studentHome.day.more', { count: foldedCount })}
            </button>
          )}
          {/* «Скло» shows what to pack as a card of its own */}
          {!day.isToday && look !== 'glass' && <PackPanel ctx={ctx} date={day.date} />}
        </>
      )}
      {open && (
        <LessonCard
          ctx={ctx}
          lesson={open.lesson}
          anchor={open.el}
          onClose={() => setOpen(undefined)}
        />
      )}
    </section>
  );
}

const kyivClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Kyiv',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

// —— the card of a lesson ——

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof User;
  label?: string;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <Icon size={17} strokeWidth={1.8} aria-hidden className="mt-0.5 shrink-0 text-muted" />
      <span className="min-w-0 flex-1 text-[14px] leading-5 text-ink">
        {label && <span className="block text-[12px] leading-4 text-muted">{label}</span>}
        {children}
      </span>
    </li>
  );
}

export const fullName = (p: { firstName: string; lastName: string; middleName: string | null }) =>
  [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ');

/**
 * A lesson's card (ref. the event card over a calendar): the day and the number of the lesson, the
 * time, where and with whom, the topic, what to bring, the homework to hand in at it and the one given
 * at it; the way in, the board, the week.
 */
/**
 * What a lesson's card shows and where it stands: under its row (above it when there is no room), a sheet
 * at the bottom on a phone; it follows its row on a scroll and closes on Escape or a click outside.
 */
export function useLessonCard(
  ctx: StudentContext,
  lesson: LessonItem,
  anchor: HTMLElement,
  onClose: () => void,
) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; phone: boolean }>();
  const today = todayIso();
  const homework = useStudentHomework(ctx.child.memberId, addDays(today, -30));
  const list = homework.data?.homework ?? [];
  const handed = new Set(
    (homework.data?.submissions ?? [])
      .filter((s) => s.status !== 'returned')
      .map((s) => s.homeworkId),
  );
  const due = list.filter((h) => h.courseId === lesson.courseId && h.dueOn === lesson.date);
  const given = list.filter(
    (h) =>
      h.courseId === lesson.courseId && h.lessonDate === lesson.date && h.dueOn !== lesson.date,
  );
  const join = canJoinLesson(lesson, today) && Date.parse(lesson.endsAt) > Date.now();
  const board = canOpenClassBoard(lesson, today);
  const who = lesson.substitute ?? lesson.teachers[0];

  useLayoutEffect(() => {
    const place = () => {
      const card = ref.current;
      if (!card) return;
      const r = anchor.getBoundingClientRect();
      const phone = window.innerWidth < 640;
      const w = card.offsetWidth;
      const h = card.offsetHeight;
      const left = Math.min(Math.max(12, r.right - w), window.innerWidth - w - 12);
      const below = r.bottom + 8;
      const top = below + h <= window.innerHeight - 12 ? below : Math.max(12, r.top - h - 8);
      setPos({ left, top, phone });
    };
    place();
    // the card follows its row when the panel or the page scrolls
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { capture: true, passive: true });
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, { capture: true });
    };
  }, [anchor]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const onDown = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);

  return { ref, pos, today, handed, due, given, join, board, who };
}

export function LessonCard({
  ctx,
  lesson,
  anchor,
  onClose,
}: {
  ctx: StudentContext;
  lesson: LessonItem;
  anchor: HTMLElement;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { ref, pos, today, handed, due, given, join, board, who } = useLessonCard(
    ctx,
    lesson,
    anchor,
    onClose,
  );

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={lesson.subject}
      className={`fixed z-50 flex flex-col gap-4 rounded-[20px] bg-white p-5 shadow-float ring-1 ring-stroke ${
        pos?.phone ? 'inset-x-3 bottom-3' : 'w-[360px]'
      }`}
      style={
        pos ? (pos.phone ? undefined : { left: pos.left, top: pos.top }) : { visibility: 'hidden' }
      }
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-muted">
            {dayLong(lesson.date)} · {t('studentHome.day.period', { n: lesson.period })} ·{' '}
            {timeOf(lesson.startsAt)}–{timeOf(lesson.endsAt)}
          </p>
          <h3 className="font-display text-[19px] leading-7 font-semibold text-ink">
            {lesson.subject}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="-mt-1 -mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-soft hover:text-ink"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      {(lesson.status === 'cancelled' ||
        lesson.isTest ||
        lesson.substitute ||
        lesson.format !== 'in_person') && (
        <div className="flex flex-wrap gap-1.5">
          <Changes lesson={lesson} />
        </div>
      )}
      <ul className="flex flex-col gap-3">
        {lesson.format === 'online' ? (
          <InfoRow icon={Video}>{t('studentHome.day.onlineLesson')}</InfoRow>
        ) : (
          lesson.room && <InfoRow icon={DoorOpen}>{capitalize(roomOf(t, lesson.room))}</InfoRow>
        )}
        {who && (
          <InfoRow
            icon={User}
            label={lesson.substitute ? t('studentHome.day.substituteTeacher') : undefined}
          >
            {fullName(who)}
          </InfoRow>
        )}
        {lesson.topic && (
          <InfoRow icon={BookOpen} label={t('studentHome.day.topic')}>
            {lesson.topic}
          </InfoRow>
        )}
        {lesson.bring && (
          <InfoRow icon={Backpack} label={t('studentHome.day.bring')}>
            {plain(lesson.bring)}
          </InfoRow>
        )}
        {due.map((h) => (
          <InfoRow key={h.id} icon={NotebookPen} label={t('studentHome.day.homeworkDue')}>
            <span className="flex items-start justify-between gap-2">
              <span className="min-w-0">{h.title}</span>
              {h.requiresSubmission &&
                (handed.has(h.id) ? (
                  <StatusPill tone="green" size="xs">
                    {t('studentHome.day.handed')}
                  </StatusPill>
                ) : (
                  <StatusPill tone={lesson.date < today ? 'red' : 'orange'} size="xs">
                    {t('studentHome.day.notHanded')}
                  </StatusPill>
                ))}
            </span>
          </InfoRow>
        ))}
        {given.map((h) => (
          <InfoRow key={h.id} icon={NotebookPen} label={t('studentHome.day.homeworkGiven')}>
            {h.title}
          </InfoRow>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {join && (
          <Link
            to="/lesson/$lessonId"
            params={{ lessonId: lesson.id }}
            className={buttonClass('primary')}
          >
            <Video size={16} strokeWidth={1.8} aria-hidden />
            {t('student.lessons.joinLesson')}
          </Link>
        )}
        {board && (
          <Link
            to="/class-board/$lessonId"
            params={{ lessonId: lesson.id }}
            className={buttonClass(join ? 'outline' : 'primary')}
          >
            {t('classBoard.open')}
          </Link>
        )}
        <Link to="/schedule" className={buttonClass('outline')}>
          <CalendarDays size={16} strokeWidth={1.8} aria-hidden />
          {t('studentHome.day.weekButton')}
        </Link>
      </div>
    </div>,
    document.body,
  );
}
